"use client";

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import DashboardPreview from "@/assets/Group 588.svg";
import Logo from "@/assets/Left Wrapper.svg";
import LayersLogo from "@/assets/Logomark.svg";
import SisyphusLogo from "@/assets/Logomark (1).svg";
import CircoolesLogo from "@/assets/Logomark (2).svg";
import CatalogLogo from "@/assets/Logomark (3).svg";
import CommandRLogo from "@/assets/Logomark (4).svg";
import Iphone13 from "@/assets/iPhone 13.svg";
import OrderOpsIcon from "@/assets/Frame 2147227877.svg";
import PaymentsIcon from "@/assets/Frame 2147227877 (1).svg";
import MenuIcon from "@/assets/Frame 2147227877 (2).svg";
import MarketingIcon from "@/assets/Frame 2147227877 (3).svg";
import DashboardActionImage from "@/assets/Right Illustration.svg";
import StarIcon from "@/assets/Path.svg";
import TundeProfile from "@/assets/Oval.svg";
import UcheProfile from "@/assets/Oval (1).svg";
import MarthaProfile from "@/assets/Oval (2).svg";
import MinusIcon from "@/assets/Icon.svg";
import PlusIcon from "@/assets/plus-circle.svg";
import CTAIllustration from "@/assets/Component 1.svg";
import { ArrowRight } from "lucide-react";

/**
 * FAQ Section component with expandable items
 */
const faqData = [
    { question: "What is LiveRetail?", answer: "Create your store, customize your brand, upload menus, and connect payment channels. Fast onboarding designed for restaurants of any size." },
    { question: "How quickly can I set up my restaurant?", answer: "Create your store, customize your brand, upload menus, and connect payment channels. Fast onboarding designed for restaurants of any size." },
    { question: "Do I need technical skills to use LiveRetail?", answer: "Create your store, customize your brand, upload menus, and connect payment channels. Fast onboarding designed for restaurants of any size." },
    { question: "How do payments and settlements work?", answer: "Create your store, customize your brand, upload menus, and connect payment channels. Fast onboarding designed for restaurants of any size." },
    { question: "Can multiple staff members use the system?", answer: "Create your store, customize your brand, upload menus, and connect payment channels. Fast onboarding designed for restaurants of any size." },
    { question: "Can I track performance and insights?", answer: "Create your store, customize your brand, upload menus, and connect payment channels. Fast onboarding designed for restaurants of any size." },
    { question: "Is LiveRetail customizable?", answer: "Create your store, customize your brand, upload menus, and connect payment channels. Fast onboarding designed for restaurants of any size." },
];

function FAQSection() {
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    const toggleFAQ = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <section className="px-6 py-10 md:px-12 md:py-[100px] bg-[#FAFAFA]">
            <div className="max-w-[800px] mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-[32px] md:text-[40px] font-bold text-[#0f2e2e] leading-tight mb-4">
                        Frequently Asked Question
                    </h2>
                    <p className="text-lg text-[#4a5568]">
                        Find answers to the most common questions about getting started, payments, features, and using LiveRetail for your restaurant.
                    </p>
                </div>

                <div className="space-y-0">
                    {faqData.map((faq, index) => (
                        <div key={index} className="border-b border-gray-200">
                            <button
                                onClick={() => toggleFAQ(index)}
                                className="w-full flex justify-between items-center py-6 text-left"
                            >
                                <span className="text-[#0f2e2e] font-semibold text-base md:text-lg pr-4">
                                    {faq.question}
                                </span>
                                <Image
                                    src={openIndex === index ? MinusIcon : PlusIcon}
                                    alt={openIndex === index ? "Collapse" : "Expand"}
                                    width={24}
                                    height={24}
                                    className="shrink-0"
                                />
                            </button>
                            <div
                                className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === index ? "max-h-40 pb-6" : "max-h-0"
                                    }`}
                            >
                                <p className="text-[#4a5568] text-sm md:text-base leading-relaxed">
                                    {faq.answer}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/**
 * Landing Page component displayed when no subdomain is detected
 * or when subdomain fetch fails.
 */
export default function LandingPage() {
    return (
        <div className="min-h-screen font-sans bg-[#e8f4f3]">
            {/* Header */}
            <header className="px-6 py-6 md:px-12 md:py-6">
                <div className="max-w-[1400px] mx-auto flex justify-between items-center">
                    <div className="flex items-center">
                        <Image src={Logo} alt="Liveretail Logo" height={40} />
                    </div>
                    <Link
                        href="#"
                        className="bg-[#006D77] text-white px-6 py-3.5 rounded-lg font-semibold text-sm hover:bg-[#1a4a4a] transition-colors duration-200"
                    >
                        Sign Up for Free
                    </Link>
                </div>
            </header>

            {/* Hero Section */}
            <main className="px-6 py-8 md:px-8 md:py-[18px] pb-20">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-[60px] items-center">
                    {/* Left Content */}
                    <div className="max-w-[520px] text-center lg:text-left mx-auto lg:mx-0">
                        <h1 className="text-[32px] md:text-[40px] lg:text-[52px] font-bold text-[#0f2e2e] leading-[1.15] mb-6 tracking-tight">
                            The All-In-One Revenue Engine for Modern Restaurants
                        </h1>
                        <p className="text-base md:text-lg text-[#4a5568] leading-relaxed mb-9">
                            Run your entire restaurant from one platform — manage orders,
                            process payments, automate marketing, handle menus, and boost
                            profitability
                        </p>
                        <Link
                            href="#"
                            className="inline-block bg-[#006D77] text-white px-7 py-4 rounded-lg font-semibold text-base hover:bg-[#0a6358] transition-colors duration-200"
                        >
                            Sign Up for Free
                        </Link>
                    </div>

                    {/* Right Content - Dashboard Preview */}
                    <div className="flex justify-center items-center">
                        <Image
                            src={DashboardPreview}
                            alt="Dashboard Preview"
                            className="w-full h-auto min-w-[350px] md:min-w-0"
                            priority
                        />
                    </div>
                </div>
            </main>

            {/* Logo Cloud Section */}
            <section className="bg-[#D4F1F3] px-0 py-6 md:px-12 md:py-[40px] overflow-hidden">
                {/* CSS for marquee animation */}
                <style jsx>{`
                    @keyframes marquee {
                        0% { transform: translateX(0); }
                        100% { transform: translateX(-50%); }
                    }
                    .marquee-track {
                        animation: marquee 10s linear infinite;
                    }
                `}</style>
                <div className="max-w-[1400px] mx-auto md:flex md:flex-wrap md:justify-between md:items-center md:gap-10">
                    {/* Mobile: Marquee animation */}
                    <div className="flex md:hidden marquee-track whitespace-nowrap gap-8">
                        <div className="flex items-center gap-2.5 text-[#0f2e2e] text-sm font-semibold min-w-max px-4">
                            <Image src={LayersLogo} alt="Layers" width={24} height={24} className="shrink-0" />
                            <span>Layers</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[#0f2e2e] text-sm font-semibold min-w-max px-4">
                            <Image src={SisyphusLogo} alt="Sisyphus" width={24} height={24} className="shrink-0" />
                            <span>Sisyphus</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[#0f2e2e] text-sm font-semibold min-w-max px-4">
                            <Image src={CircoolesLogo} alt="Circooles" width={24} height={24} className="shrink-0" />
                            <span>Circooles</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[#0f2e2e] text-sm font-semibold min-w-max px-4">
                            <Image src={CatalogLogo} alt="Catalog" width={24} height={24} className="shrink-0" />
                            <span>Catalog</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[#0f2e2e] text-sm font-semibold min-w-max px-4">
                            <Image src={CommandRLogo} alt="Command+R" width={24} height={24} className="shrink-0" />
                            <span>Command+R</span>
                        </div>
                        {/* Duplicate for seamless loop */}
                        <div className="flex items-center gap-2.5 text-[#0f2e2e] text-sm font-semibold min-w-max px-4">
                            <Image src={LayersLogo} alt="Layers" width={24} height={24} className="shrink-0" />
                            <span>Layers</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[#0f2e2e] text-sm font-semibold min-w-max px-4">
                            <Image src={SisyphusLogo} alt="Sisyphus" width={24} height={24} className="shrink-0" />
                            <span>Sisyphus</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[#0f2e2e] text-sm font-semibold min-w-max px-4">
                            <Image src={CircoolesLogo} alt="Circooles" width={24} height={24} className="shrink-0" />
                            <span>Circooles</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[#0f2e2e] text-sm font-semibold min-w-max px-4">
                            <Image src={CatalogLogo} alt="Catalog" width={24} height={24} className="shrink-0" />
                            <span>Catalog</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[#0f2e2e] text-sm font-semibold min-w-max px-4">
                            <Image src={CommandRLogo} alt="Command+R" width={24} height={24} className="shrink-0" />
                            <span>Command+R</span>
                        </div>
                    </div>
                    {/* Desktop: Normal layout */}
                    <div className="hidden md:flex items-center gap-2.5 text-[#0f2e2e] text-lg font-semibold min-w-max">
                        <Image src={LayersLogo} alt="Layers" width={24} height={24} className="shrink-0" />
                        <span className="whitespace-nowrap">Layers</span>
                    </div>
                    <div className="hidden md:flex items-center gap-2.5 text-[#0f2e2e] text-lg font-semibold min-w-max">
                        <Image src={SisyphusLogo} alt="Sisyphus" width={24} height={24} className="shrink-0" />
                        <span className="whitespace-nowrap">Sisyphus</span>
                    </div>
                    <div className="hidden md:flex items-center gap-2.5 text-[#0f2e2e] text-lg font-semibold min-w-max">
                        <Image src={CircoolesLogo} alt="Circooles" width={24} height={24} className="shrink-0" />
                        <span className="whitespace-nowrap">Circooles</span>
                    </div>
                    <div className="hidden md:flex items-center gap-2.5 text-[#0f2e2e] text-lg font-semibold min-w-max">
                        <Image src={CatalogLogo} alt="Catalog" width={24} height={24} className="shrink-0" />
                        <span className="whitespace-nowrap">Catalog</span>
                    </div>
                    <div className="hidden md:flex items-center gap-2.5 text-[#0f2e2e] text-lg font-semibold min-w-max">
                        <Image src={CommandRLogo} alt="Command+R" width={24} height={24} className="shrink-0" />
                        <span className="whitespace-nowrap">Command+R</span>
                    </div>
                </div>
            </section>

            {/* Never Miss a DM Section */}
            <section className="px-6 py-10 md:px-12 md:py-[40px] bg-[#FAFAFA]">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-[60px] items-center">
                    {/* Left Content */}
                    <div className="max-w-[580px] text-center lg:text-left mx-auto lg:mx-0 order-2 lg:order-1">
                        <h2 className="text-[32px] md:text-[40px] lg:text-[56px] font-bold text-[#0f2e2e] leading-[1.15] mb-6 tracking-tight">
                            Never Miss a DM Again <br className="hidden lg:block" />
                            Close Orders Automatically
                        </h2>
                        <p className="text-base md:text-lg text-[#4a5568] leading-relaxed mb-9">
                            Your customers love messaging apps. LiveRetail turns Instagram DMs,
                            Facebook messages, and WhatsApp chats into automated ordering
                            channels — no staff needed.
                        </p>
                        <Link
                            href="#"
                            className="inline-block bg-[#0f7b6c] text-white px-7 py-4 rounded-lg font-semibold text-base hover:bg-[#0a6358] transition-colors duration-200"
                        >
                            Try Now for Free
                        </Link>
                    </div>

                    {/* Right Content - iPhone Preview */}
                    <div className="flex justify-center items-center order-1 lg:order-2">
                        <Image
                            src={Iphone13}
                            alt="iPhone Preview"
                            className="w-full max-w-[400px] lg:max-w-none h-auto drop-shadow-2xl"
                        />
                    </div>
                </div>
            </section>

            {/* Features Grid Section */}
            <section className="px-6 py-10 md:px-12 md:py-[100px] bg-white">
                <div className="max-w-[1400px] mx-auto">
                    <div className="text-center max-w-[900px] mx-auto mb-16">
                        <h2 className="text-[32px] md:text-[40px] font-bold text-[#0f2e2e] leading-tight mb-6">
                            Everything Your Restaurant Needs. All in One Platform.
                        </h2>
                        <p className="text-lg text-[#4a5568] leading-relaxed">
                            From orders to payouts to marketing, LiveRetail gives you every tool
                            you need to operate smarter, faster, and more profitably.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-[1200px] mx-auto">
                        {/* Card 1: Order & Operations */}
                        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-[0px_4px_20px_rgba(0,0,0,0.02)] hover:shadow-[0px_10px_30px_rgba(0,0,0,0.05)] transition-shadow duration-300 flex flex-col items-center text-center">
                            <div className="mb-6">
                                <Image src={OrderOpsIcon} alt="Order & Operations" width={64} height={64} />
                            </div>
                            <h3 className="text-xl font-bold text-[#0f2e2e] mb-4">
                                Order & Operations Management
                            </h3>
                            <p className="text-[#4a5568] leading-relaxed mb-8">
                                Centralize all your orders, dining, takeout, delivery, and WhatsApp
                                in one dashboard. Real time order tracking, Staff assignment, Order
                                delays alerts, Kitchen workflow.
                            </p>
                            <Link
                                href="#"
                                className="mt-auto inline-flex items-center text-[#0f7b6c] font-semibold hover:text-[#0a6358] transition-colors"
                            >
                                See how it works <ArrowRight className="ml-2 w-4 h-4" />
                            </Link>
                        </div>

                        {/* Card 2: Payments & Settlements */}
                        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-[0px_4px_20px_rgba(0,0,0,0.02)] hover:shadow-[0px_10px_30px_rgba(0,0,0,0.05)] transition-shadow duration-300 flex flex-col items-center text-center">
                            <div className="mb-6">
                                <Image src={PaymentsIcon} alt="Payments & Settlements" width={64} height={64} />
                            </div>
                            <h3 className="text-xl font-bold text-[#0f2e2e] mb-4">
                                Payments & Settlements
                            </h3>
                            <p className="text-[#4a5568] leading-relaxed mb-8">
                                Fast, reliable payments with automated settlements into your
                                restaurant wallet. Wallet balance, Settlements, Refunds, Payments
                                links and Virtual accounts.
                            </p>
                            <Link
                                href="#"
                                className="mt-auto inline-flex items-center text-[#0f7b6c] font-semibold hover:text-[#0a6358] transition-colors"
                            >
                                Explore Payments <ArrowRight className="ml-2 w-4 h-4" />
                            </Link>
                        </div>

                        {/* Card 3: Menu & Inventory Management */}
                        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-[0px_4px_20px_rgba(0,0,0,0.02)] hover:shadow-[0px_10px_30px_rgba(0,0,0,0.05)] transition-shadow duration-300 flex flex-col items-center text-center">
                            <div className="mb-6">
                                <Image src={MenuIcon} alt="Menu & Inventory" width={64} height={64} />
                            </div>
                            <h3 className="text-xl font-bold text-[#0f2e2e] mb-4">
                                Menu & Inventory Management
                            </h3>
                            <p className="text-[#4a5568] leading-relaxed mb-8">
                                Update menus instantly and track stock levels with smart alerts.
                                Real-time menu editing, Out-of-stock alerts, Price updates and
                                Multiple location menus
                            </p>
                            <Link
                                href="#"
                                className="mt-auto inline-flex items-center text-[#0f7b6c] font-semibold hover:text-[#0a6358] transition-colors"
                            >
                                See how it works <ArrowRight className="ml-2 w-4 h-4" />
                            </Link>
                        </div>

                        {/* Card 4: Marketing & Customer Engagement */}
                        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-[0px_4px_20px_rgba(0,0,0,0.02)] hover:shadow-[0px_10px_30px_rgba(0,0,0,0.05)] transition-shadow duration-300 flex flex-col items-center text-center">
                            <div className="mb-6">
                                <Image src={MarketingIcon} alt="Marketing & Customer Engagement" width={64} height={64} />
                            </div>
                            <h3 className="text-xl font-bold text-[#0f2e2e] mb-4">
                                Marketing & Customer Engagement
                            </h3>
                            <p className="text-[#4a5568] leading-relaxed mb-8">
                                Targeted campaigns and grow customer loyalty with AI suggestions.
                                WhatsApp, SMS, Email, Push, Smart segments, Automated insights and
                                Campaign perfomance
                            </p>
                            <Link
                                href="#"
                                className="mt-auto inline-flex items-center text-[#0f7b6c] font-semibold hover:text-[#0a6358] transition-colors"
                            >
                                See how it works <ArrowRight className="ml-2 w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* How it Works Section */}
            <section className="px-6 py-10 md:px-12 md:py-[80px] bg-[#FAFAFA]">
                <div className="max-w-[1200px] mx-auto">
                    <div className="text-center max-w-[800px] mx-auto mb-16">
                        <h2 className="text-[32px] md:text-[40px] font-bold text-[#0f2e2e] leading-tight mb-4">
                            How it Works
                        </h2>
                        <p className="text-lg text-[#4a5568]">
                            See How Restaurants Run With LiveRetail in 3 simple steps.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Step 1 */}
                        <div className="bg-white rounded-2xl p-8 border border-gray-100 flex flex-col items-start text-left h-full shadow-sm hover:shadow-md transition-shadow">
                            <div className="bg-[#1A202C] text-white w-12 h-12 rounded-lg flex items-center justify-center text-xl font-bold mb-6">
                                1
                            </div>
                            <h3 className="text-xl font-bold text-[#0f2e2e] mb-4">
                                Set Up Your Restaurant
                            </h3>
                            <p className="text-[#4a5568] leading-relaxed text-sm md:text-base">
                                Create your store, customize your brand, upload menus, and
                                connect payment channels. Fast onboarding designed for
                                restaurants of any size.
                            </p>
                        </div>

                        {/* Step 2 */}
                        <div className="bg-white rounded-2xl p-8 border border-gray-100 flex flex-col items-start text-left h-full shadow-sm hover:shadow-md transition-shadow">
                            <div className="bg-[#1A202C] text-white w-12 h-12 rounded-lg flex items-center justify-center text-xl font-bold mb-6">
                                2
                            </div>
                            <h3 className="text-xl font-bold text-[#0f2e2e] mb-4">
                                Manage & Automate Operations
                            </h3>
                            <p className="text-[#4a5568] leading-relaxed text-sm md:text-base">
                                Handle orders, track payments, manage menus, and automate
                                customer engagement, all from one dashboard. Your entire
                                restaurant on a one platform.
                            </p>
                        </div>

                        {/* Step 3 */}
                        <div className="bg-white rounded-2xl p-8 border border-gray-100 flex flex-col items-start text-left h-full shadow-sm hover:shadow-md transition-shadow">
                            <div className="bg-[#1A202C] text-white w-12 h-12 rounded-lg flex items-center justify-center text-xl font-bold mb-6">
                                3
                            </div>
                            <h3 className="text-xl font-bold text-[#0f2e2e] mb-4">
                                Grow With AI Insights
                            </h3>
                            <p className="text-[#4a5568] leading-relaxed text-sm md:text-base">
                                See what’s working, identify customer trends, and launch
                                targeted campaigns powered by AI. Boost repeat sales and
                                increase engagement effortlessly.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* See LiveRetail in Action Section */}
            <section className="px-6 py-10 md:px-10 md:py-[40px] bg-[#004349]">
                <div className="max-w-[1400px] mx-auto text-center">
                    <div className="max-w-[900px] mx-auto mb-16">
                        <h2 className="text-[32px] md:text-[40px] font-bold text-white leading-tight mb-6">
                            See LiveRetail in Action
                        </h2>
                        <p className="text-lg text-white/90 leading-relaxed">
                            A clean, powerful dashboard designed to simplify your entire
                            restaurant workflow. Manage orders, menus, payments, marketing,
                            and insights effortlessly, all from one centralized platform.
                        </p>
                    </div>

                    <div className="flex justify-center">
                        <Image
                            src={DashboardActionImage}
                            alt="LiveRetail Dashboard"
                            className="w-full h-auto max-w-[1000px] drop-shadow-2xl"
                        />
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="px-6 py-10 md:px-10 md:py-[40px] bg-[#FAFAFA]">
                <div className="max-w-[1200px] mx-auto">
                    <div className="text-center max-w-[800px] mx-auto mb-16">
                        <h2 className="text-[32px] md:text-[40px] font-bold text-[#0f2e2e] leading-tight mb-4">
                            What Restaurants Are Saying About Us
                        </h2>
                        <p className="text-lg text-[#4a5568]">
                            Honest feedback from real users improving their daily operations with LiveRetail.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                        {/* Testimonial 1 - Tunde */}
                        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                            <div className="flex gap-1 mb-6">
                                {[...Array(5)].map((_, i) => (
                                    <Image key={i} src={StarIcon} alt="star" width={16} height={16} />
                                ))}
                            </div>
                            <p className="text-[#4a5568] leading-relaxed mb-8 text-sm md:text-base">
                                "Before LiveRetail, our orders were everywhere — WhatsApp, calls, walk-ins. Now everything comes to one screen. It's made our kitchen work stress-free."
                            </p>
                            <div className="flex items-center gap-3">
                                <Image src={TundeProfile} alt="Tunde" width={44} height={44} className="rounded-full" />
                                <div>
                                    <p className="font-semibold text-[#0f2e2e]">Tunde,</p>
                                    <p className="text-sm text-[#4a5568]">ChopNation Foods</p>
                                </div>
                            </div>
                        </div>

                        {/* Testimonial 2 - Uche */}
                        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                            <div className="flex gap-1 mb-6">
                                {[...Array(5)].map((_, i) => (
                                    <Image key={i} src={StarIcon} alt="star" width={16} height={16} />
                                ))}
                            </div>
                            <p className="text-[#4a5568] leading-relaxed mb-8 text-sm md:text-base">
                                "We didn't know why sales were going up or down. LiveRetail shows us exactly what's selling, who's buying, and what to promote. It's changed our business."
                            </p>
                            <div className="flex items-center gap-3">
                                <Image src={UcheProfile} alt="Uche" width={44} height={44} className="rounded-full" />
                                <div>
                                    <p className="font-semibold text-[#0f2e2e]">Uche,</p>
                                    <p className="text-sm text-[#4a5568]">Urban bites,Lagos</p>
                                </div>
                            </div>
                        </div>

                        {/* Testimonial 3 - Martha */}
                        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
                            <div className="flex gap-1 mb-6">
                                {[...Array(5)].map((_, i) => (
                                    <Image key={i} src={StarIcon} alt="star" width={16} height={16} />
                                ))}
                            </div>
                            <p className="text-[#4a5568] leading-relaxed mb-8 text-sm md:text-base">
                                "No more calling banks or checking five apps. I see my settlements and wallet instantly. It's the transparency I never had before."
                            </p>
                            <div className="flex items-center gap-3">
                                <Image src={MarthaProfile} alt="Martha" width={44} height={44} className="rounded-full" />
                                <div>
                                    <p className="font-semibold text-[#0f2e2e]">Martha,</p>
                                    <p className="text-sm text-[#4a5568]">SweetTreat Café</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="text-center">
                        <button className="inline-flex items-center text-[#0f2e2e] font-semibold border-b-2 border-[#0f2e2e] pb-1 hover:opacity-80 transition-opacity">
                            Check all 2,157 reviews
                        </button>
                    </div>
                </div>
            </section>

            {/* Results Stats Section */}
            <section className="px-6 py-10 md:px-12 md:py-[80px] bg-[#004349]">
                <div className="max-w-[1200px] mx-auto">
                    <h2 className="text-[32px] md:text-[40px] font-bold text-white leading-tight mb-16 text-center">
                        The Results Speak for Themselves
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
                        {/* Stat 1 */}
                        <div className="text-center md:border-r md:border-white/20 px-4">
                            <p className="text-[48px] md:text-[56px] font-bold text-white mb-4">40%</p>
                            <p className="text-white/90 text-sm md:text-base leading-relaxed">
                                <span className="font-semibold">Faster order processing.</span> Streamlined operations help restaurants reduce delays and serve customers quicker.
                            </p>
                        </div>

                        {/* Stat 2 */}
                        <div className="text-center md:border-r md:border-white/20 px-4">
                            <p className="text-[48px] md:text-[56px] font-bold text-white mb-4">₦12M+</p>
                            <p className="text-white/90 text-sm md:text-base leading-relaxed">
                                <span className="font-semibold">Processed Monthly.</span> Seamless payments and transparent settlements build trust and stability.
                            </p>
                        </div>

                        {/* Stat 3 */}
                        <div className="text-center px-4">
                            <p className="text-[48px] md:text-[56px] font-bold text-white mb-4">5k+</p>
                            <p className="text-white/90 text-sm md:text-base leading-relaxed">
                                <span className="font-semibold">Successful Campaign.</span> Helping restaurants engage customers and boost sales effortlessly.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <FAQSection />

            {/* CTA Section */}
            <section className="px-6 py-10 md:px-12 md:py-[40px] bg-[#e8f4f3]">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-[60px] items-center">
                    {/* Left - Illustration */}
                    <div className="flex justify-center lg:justify-start order-2 lg:order-1">
                        <Image
                            src={CTAIllustration}
                            alt="Transform your restaurant"
                            className="w-full max-w-[500px] h-auto"
                        />
                    </div>

                    {/* Right - Content */}
                    <div className="max-w-[580px] text-center lg:text-left mx-auto lg:mx-0 order-1 lg:order-2">
                        <h2 className="text-[32px] md:text-[40px] lg:text-[48px] font-bold text-[#0f2e2e] leading-[1.15] mb-6 tracking-tight">
                            Ready to Transform Your Restaurant?
                        </h2>
                        <p className="text-base md:text-lg text-[#4a5568] leading-relaxed mb-9">
                            Join thousands of food businesses using LiveRetail to manage operations, grow sales, and deliver better customer experiences.
                        </p>
                        <Link
                            href="#"
                            className="inline-block bg-[#006D77] text-white px-7 py-4 rounded-lg font-semibold text-base hover:bg-[#0a6358] transition-colors duration-200"
                        >
                            Get Started for Free
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer Section */}
            <footer className="px-6 py-10 md:px-12 md:py-16 bg-white">
                <div className="max-w-[1400px] mx-auto">
                    {/* Logo */}
                    <div className="mb-8">
                        <Image src={Logo} alt="Liveretail Logo" height={40} />
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-200 pt-8">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                            {/* Left - Tagline and Description */}
                            <div className="max-w-[600px]">
                                <h3 className="text-lg font-semibold text-[#0f2e2e] mb-2">
                                    Move faster with Liveretail
                                </h3>
                                <p className="text-sm text-[#4a5568] leading-relaxed">
                                    Tools built to help restaurants manage operations, accept payments, engage customers, and grow with confidence.
                                </p>
                            </div>

                            {/* Right - Copyright */}
                            <p className="text-sm text-[#4a5568]">
                                © {new Date().getFullYear()} Untitled UI. All rights reserved.
                            </p>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
